import { useMemo } from 'react';
import { useAppContext } from '@/app/providers/AppContextProvider';
import { isFeatureActive } from '@/lib/featureFlags';
import type { SettingType } from '@/types/app';
import { FeatureFlag } from '@/types/app';
import {
	getNextGuidanceStep,
	getNextPendingStep,
	getNextUnconfirmedStep,
	isStepPending,
	type WizardFlags,
	type WizardSources
} from '../services/confirmationWizard';

export interface ConfirmationWizard {
	sources: WizardSources;
	flags: WizardFlags;
	isStepPending: (step: SettingType) => boolean;
	getNextPendingStep: (currentStep: SettingType) => SettingType | null;
	getNextUnconfirmedStep: (
		currentStep: SettingType,
		confirmedSteps: ReadonlySet<SettingType>
	) => SettingType | null;
	getNextGuidanceStep: (confirmedSteps: ReadonlySet<SettingType>) => SettingType | null;
}

/**
 * Binds the confirmation-wizard helpers to the current quote's sources and origin
 * feature flags, so components can decide whether selecting a quote opens a pending
 * step and which step a wizard button should advance to.
 */
export function useConfirmationWizard(): ConfirmationWizard {
	const { appData, config } = useAppContext();

	// Until the form's premium variables are loaded we don't know the warning state, so
	// treat nothing as pending rather than routing the wizard on unknown data.
	const premiumVariablesLoaded = appData?.premium_variables != null;

	const sources: WizardSources = useMemo(
		() => ({
			employees: appData?.premium_variables?.employees?.source ?? null,
			absence: appData?.premium_variables?.absence_info?.source ?? null,
			coverage: appData?.premium_variables?.coverage?.sick_leave_short_term?.source ?? null
		}),
		[
			appData?.premium_variables?.employees?.source,
			appData?.premium_variables?.absence_info?.source,
			appData?.premium_variables?.coverage?.sick_leave_short_term?.source
		]
	);

	const allowUserCoverageOverride = isFeatureActive(
		config,
		FeatureFlag.ALLOW_USER_COVERAGE_OVERRIDE
	);
	const flags: WizardFlags = useMemo(
		() => ({ allowUserCoverageOverride }),
		[allowUserCoverageOverride]
	);

	return useMemo(
		() => ({
			sources,
			flags,
			isStepPending: (step: SettingType) =>
				premiumVariablesLoaded && isStepPending(step, sources, flags),
			getNextPendingStep: (currentStep: SettingType) =>
				premiumVariablesLoaded ? getNextPendingStep(currentStep, sources, flags) : null,
			getNextUnconfirmedStep: (
				currentStep: SettingType,
				confirmedSteps: ReadonlySet<SettingType>
			) =>
				premiumVariablesLoaded
					? getNextUnconfirmedStep(currentStep, sources, flags, confirmedSteps)
					: null,
			getNextGuidanceStep: (confirmedSteps: ReadonlySet<SettingType>) =>
				premiumVariablesLoaded ? getNextGuidanceStep(sources, flags, confirmedSteps) : null
		}),
		[sources, flags, premiumVariablesLoaded]
	);
}
